/**
 * 帳款明細表(`ar-detail`,未勾「依客戶分別列印」)的列印抬頭(REQ-20260831004)。
 *
 * 使用者拿標註圖來,要把這張表印出來的樣子改成舊會計系統那種「一份完整表頭」:
 * 我方公司在最上面、報表名稱置中、下面一排雙方基本資料與帳款區間。
 * 同一次把中段那塊重複的報表名稱 / 客戶小字刪掉(那是 REQ-20260831002 加的),
 * 資訊改成只在這裡出現一次。
 *
 * 兩個實作前提:
 *  - **整塊 `hidden print:block`** —— 畫面上不出現。篩選器(客戶挑選器、起訖日)
 *    就在這個區塊上方,再顯示一次只是佔位;列印時看不到篩選器才需要補。
 *    REQ-20260902001 起多了 `mode="screen"`:勾「依客戶分別列印」時,每位客戶
 *    **螢幕上**那張卡片也要用同一份抬頭,那時整塊反過來變成 `print:hidden` ——
 *    卡片裡列印那份由 `ArDetailPrintPages` 負責,螢幕這份印出來會變成兩份抬頭。
 *  - **資料全部已經在前端手上**,沒有為了這個抬頭去改後端:公司四項來自既有的
 *    `useCompanyProfile()`(REQ-20260817004),客戶各欄來自 `ReportsPanel` 的
 *    `customer` state —— 那是 `PartyPicker` 給的 `PartyInfo`(REQ-20260824010),
 *    本來就含編號 / 名稱 / 聯絡人 / 電話 / 統編 / 收款憑證。
 *    REQ-20260901013 起還有第二個來源:勾「依客戶分別列印」時,每位客戶的六格
 *    從明細列上取(`partyCode` / `partyFullName` / …),所以 prop 型別收窄成
 *    `ArDetailHeaderParty` —— 見下方。
 *
 * 沒選客戶、或某一格是空的就印「—」,不擋住列印 —— 與分組版面的抬頭同一條
 * (REQ-20260817003 §三.4)。
 */
import type { PartyInfo } from '@app/shared';
import { useCompanyProfile } from '../accounting/api';

/**
 * 抬頭真的用得到的那六格(REQ-20260901013)。
 *
 * 原本這個 prop 的型別是整個 `PartyInfo` —— 未勾分組列印時客戶來自 `PartyPicker`,
 * 手上本來就有完整的一份。勾分組之後每位客戶的資料是從**明細列**上取的
 * (`partyCode` / `partyFullName` / `partyContact`…),沒有 `id` / `type` / `phones`
 * 這些欄位。收窄成 `Pick<>` 而不是去捏假的 `id` 與空 `phones` 陣列:
 * 捏出來的值不會被用到,但下一個人讀到 `id: 0` 只會以為那是真的。
 * `PartyInfo` 結構相容,單一客戶那條路徑傳的仍是它,行為一字不差。
 */
export type ArDetailHeaderParty = Pick<
  PartyInfo,
  'code' | 'fullName' | 'contact' | 'phone' | 'taxId' | 'paymentCert'
>;

/** 兩種模式都要的四項 */
interface ArDetailHeaderBase {
  /** 報表名稱,由呼叫端傳入下拉選單的 label,不在這裡再寫死一份 */
  reportLabel: string;
  customer: ArDetailHeaderParty | null;
  /** 兩者都是 `<input type="date">` 的值(`YYYY-MM-DD`),留白 = 不限 */
  from: string;
  to: string;
}

/**
 * 頁數那兩格**只存在於列印模式**,而且是必填的(REQ-20260902001)。
 *
 * 用 discriminated union 而不是把兩者改成選填 + 預設值:有預設值就等於把
 * REQ-20260831004 §四.1 否決的「1 / 1」寫回程式裡。螢幕模式根本沒有分頁可言,
 * 連傳都不該傳 —— 傳了會直接 tsc 紅,不是被默默忽略。
 */
export type ArDetailPrintHeaderProps = ArDetailHeaderBase &
  (
    | {
        /** 預設。整塊 `hidden print:block`,只出現在紙上 */
        mode?: 'print';
        /**
         * 這是第幾頁 / 一共幾頁(REQ-20260901002)。兩個都是**必填** ——
         * 給預設值等於把「1 / 1」寫回程式裡,而那正是 REQ-20260831004 §四.1 否決的東西。
         * 呼叫端只有 `ArDetailPrintPages`,它自己裝箱、自己知道分母。
         */
        pageNo: number;
        pageCount: number;
      }
    | {
        /** 螢幕模式:整塊 `print:hidden`,而且**不印頁數**(螢幕沒有分頁) */
        mode: 'screen';
      }
  );

export function ArDetailPrintHeader(props: ArDetailPrintHeaderProps): JSX.Element {
  const { reportLabel, customer, from, to } = props;
  // 抬頭沒設定過(或這支查失敗)時就留白 —— 不擋住整張報表列印
  const { data: company } = useCompanyProfile();

  return (
    <div
      // 螢幕模式把 `mb-3` 排在前面不是隨手寫的:jsdom(nwsapi)的 `:scope` 是拿
      // 元素的**第一個** class 拼出來的,`print:hidden` 會被讀成 `.print` + 未知
      // 偽類 `:hidden` 而讓 `querySelectorAll(':scope > …')` 直接丟 SyntaxError。
      // CSS 不在意 class 的先後,測試在意 —— 列印那份第一個 class 剛好是 `hidden`。
      className={props.mode === 'screen' ? 'mb-3 print:hidden' : 'hidden print:block mb-3'}
      data-testid="ar-detail-print-header"
    >
      <div className="text-center text-lg font-semibold text-slate-900">{company?.name || '—'}</div>
      <div className="text-center text-xs text-slate-600">
        電話：{company?.phone || '—'}　傳真：{company?.fax || '—'}
      </div>
      <div className="text-center text-base font-medium mt-1 mb-2">{reportLabel}</div>
      {/* 左欄客戶身分、中欄聯絡與帳務條件、右欄查詢區間 —— 舊會計系統表頭的排法 */}
      {/* gap-x-4:三欄等寬,欄距每少 8px 第三欄就多 2.7px —— 帳款區間才不會折行 */}
      <div className="grid grid-cols-3 gap-x-4 gap-y-0.5 text-xs">
        <div>
          <Field label="客戶編號" value={customer?.code} />
          <Field label="客戶名稱" value={customer?.fullName} />
          <Field label="聯 絡 人" value={customer?.contact} />
        </div>
        <div>
          <Field label="連絡電話" value={customer?.phone} />
          <Field label="統一編號" value={customer?.taxId} />
          <Field label="收款憑證" value={customer?.paymentCert} />
        </div>
        <div>
          {/* 頁數在上、帳款區間下移一列(REQ-20260831008 標註圖第 2、3 點)。
              分母由 `ArDetailPrintPages` 的裝箱結果給,不是猜的 —— 見本檔最下方。
              螢幕模式整格不渲染:那份沒有分頁,印任何數字都是假的 */}
          {props.mode === 'screen' ? null : (
            <Field label="頁數" value={`${props.pageNo} / ${props.pageCount}`} />
          )}
          <Field label="帳款區間" value={rangeLabel(from, to)} />
        </div>
      </div>
    </div>
  );
}

/**
 * 帳款區間的顯示字串。**純字串處理,不碰 `new Date()`** ——
 * `<input type="date">` 的值本來就是使用者看到的那一天,轉成 Date 再格式化只會
 * 因為容器跑 UTC 而差一天(CLAUDE.md §6.1)。
 *
 * 四種寫法與檔名的 `dateSegment()`(REQ-20260831003)同一套語意,但**沒有共用**:
 * 那邊要的是檔名片段(緊湊、無空白、`YYYYMMDD`),這邊是給人看的表頭。
 */
function rangeLabel(from: string, to: string): string {
  const f = from.replaceAll('-', '/');
  const t = to.replaceAll('-', '/');
  if (f && t) return `${f} - ${t}`;
  if (f) return `${f} 起`;
  if (t) return `至 ${t}`;
  return '全部日期';
}

/**
 * 抬頭的一格。沒填的欄位印「—」,與分組版面 / 月結對帳單抬頭同一種寫法。
 *
 * label 給**固定寬度**(REQ-20260831008 標註圖第 1 點):`shrink-0` 只保證 label
 * 不被壓縮,三個 label 實際字寬不一樣(「聯 絡 人」帶手動空白、其餘四個字),
 * 值就從三個不同的 x 起算。固定 `w-12`(= 四個中文字的寬度)之後同一欄三行的值切齊。
 * 寬度取 `w-12` 不是更寬的級距:第三欄要放得下「2026/06/01 - 2026/08/31」,
 * label 每多 8px,那串就少 8px 可用寬 —— 實測 `w-16` 會把它擠成兩行。
 */
function Field({ label, value }: { label: string; value: string | null | undefined }): JSX.Element {
  return (
    <div className="flex gap-2">
      <span className="text-slate-500 w-12 shrink-0">{label}</span>
      <span className="text-slate-900">{value || '—'}</span>
    </div>
  );
}

/*
 * ── 為什麼「頁數」現在有分母了 ─────────────────────────────────────────────
 *
 * REQ-20260831008 把這一欄做成只印「頁數 1」,因為總頁數量不到;
 * REQ-20260901002 使用者指名要 `1 / 2` 這種分子分母,於是**換了一條路**:
 * 不再預測瀏覽器會在哪裡分頁,而是由 `ArDetailPrintPages` 自己把品項列裝箱成
 * 固定張數的 A4 區塊(每塊 `break-after: page`)。**分母是我們決定的,不是猜的**,
 * 所以印它不違反 REQ-20260831004 §四.1 的那條原則(不印會騙人的頁數)——
 * 那條原則反對的是「算出來卻會算錯」,不是反對分母本身。
 *
 * 舊路線的失敗紀錄留著,免得下一個人再走一次。三條都用真 Chrome 量過
 * (REQ-20260831008 §三有數據),共通點是它們都在**預測**瀏覽器的分頁位置:
 *  1. `beforeprint` 時量 `100vh` 探針 —— Chrome 在那個時點**還沒套用列印版面**
 *     (`matchMedia('print').matches` 是 false、`100vh` 回螢幕視窗高)。
 *  2. 離屏 clone 排到 A4 內容寬,`ceil(高 / 可印高)` —— 66 組資料只中 31 組,
 *     一律低估:`tr` 有 `break-inside: avoid`,換頁的空隙不會被算進去。
 *  3. 同上再加逐列裝箱 —— 中 46 / 66,仍低估:瀏覽器**每一頁都會重印
 *     `thead` 與 `tfoot`**(實測合計列出現在每一頁底部)。
 *
 * 真正的死結是兩個 JS 讀不到的未知數:Chrome 為了把過寬的表格塞進紙寬而做的
 * 自動縮放比例,以及使用者在列印對話框選的紙張邊界。REQ-20260831008 §七 因此
 * 寫下走自行裝箱這條路的兩個前提,兩個都在 REQ-20260901002 一併滿足:
 *  (a) 表格窄到不需要縮放 —— 列印版面的儲存格水平 padding 收成 `px-2`;
 *  (b) 接受紙張邊界固定 —— `ArDetailPrintPages` 自己掛 `@page { margin: 10mm }`,
 *      不支援使用者在列印對話框自訂邊界。
 *
 * 前提破掉(例如有人把欄位加寬到超過可印寬)分母就會開始說謊。改十欄之前先看
 * docs/REQ-20260901002 §四 的量測數據。
 */
